import { memo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedJobId } from "@/store/selectedJobIdSlice";
import type { RootState } from "../../../app/store";
import type { JobCardProps } from "./types";
import JobCardHeader from "./JobCardHeader";
import JobCardDescription from "./JobCardDescription";
import JobCardSkills from "./JobCardSkills";
import { cn } from "@cointervue/design-system";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@cointervue/design-system/components";

function JobCard({ job, className }: JobCardProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: selectedJobId } = useSelector(
    (state: RootState) => state.selectedJobId
  );

  const {
    jobId,
    jobTitle,
    status,
    location,
    createdAt,
    experienceRequired,
    jobDescription,
    skills,
  } = job;

  const handleViewApplicants = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      dispatch(setSelectedJobId({ id: jobId }));
      navigate(`/jobs/${jobId}`);
    },
    [dispatch, jobId, navigate]
  );

  const handelEditJobRole = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      navigate(`/jobs/edit/${jobId}`);
    },
    [navigate, jobId]
  );

  const isActive = selectedJobId === jobId;

  const handleSelectedJobCard = useCallback(() => {
    dispatch(setSelectedJobId({ id: jobId }));
  }, [dispatch, jobId]);

  const cardClasses = cn(
    "p-4 mx-4 mb-3 space-y-2",
    isActive ? "border-brand-dark-orange" : "border-brand-gray-light",
    className,
  );

  return (
    <Card onClick={handleSelectedJobCard} className={cardClasses}>
      <CardHeader className="flex-row justify-between gap-3 p-0">
        <JobCardHeader
          jobTitle={jobTitle}
          status={status}
          onEdit={handelEditJobRole}
          onViewApplicants={handleViewApplicants}
        />
      </CardHeader>
      <CardContent className="p-0">
        <JobCardDescription
          description={jobDescription}
          location={location}
          createdAt={createdAt}
          experience={experienceRequired}
        />
      </CardContent>
      <CardFooter className="p-0 flex-col items-start">
        <JobCardSkills skills={skills} />
      </CardFooter>
    </Card>
  );
}

export default memo(JobCard);